/**
 * Chart Data Transformers
 * Functions to transform snapshot data into formats ready for Recharts
 */

import { formatChartValue, formatCurrencyText, formatPercentage } from '../chartFormatters';

/**
 * Format month string for display
 * @param {string} monthStr - Month in YYYY-MM format
 * @returns {string} Formatted month (e.g., "Jan 2026")
 */
export const formatMonthLabel = (monthStr) => {
  if (!monthStr) return '';

  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return `${monthNames[date.getMonth()]} ${year}`;
};

/**
 * Transform snapshots for net worth trend line chart
 * @param {Array} snapshots - Array of snapshot objects
 * @returns {Array} Chart data ready for Recharts LineChart
 */
export const transformForNetWorthChart = (snapshots = []) => {
  if (!Array.isArray(snapshots) || snapshots.length === 0) {
    return [];
  }

  // Sort by month ascending
  const sorted = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));

  return sorted.map(snapshot => ({
    month: formatMonthLabel(snapshot.month),
    monthKey: snapshot.month,
    netWorth: snapshot.netWorth || 0,
    totalAssets: snapshot.totalAssets || 0,
    totalLiabilities: snapshot.totalLiabilities || 0,

    // Include raw values for tooltips
    _raw: {
      netWorth: snapshot.netWorth || 0,
      totalAssets: snapshot.totalAssets || 0,
      totalLiabilities: snapshot.totalLiabilities || 0
    }
  }));
};

/**
 * Transform snapshots for growth attribution stacked bar chart
 * @param {Array} snapshots - Array of snapshot objects
 * @returns {Array} Chart data ready for Recharts BarChart
 */
export const transformForAttributionChart = (snapshots = []) => {
  if (!Array.isArray(snapshots) || snapshots.length === 0) {
    return [];
  }

  // Sort by month ascending
  const sorted = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));

  return sorted.map(snapshot => {
    const changes = snapshot.changes || {};

    return {
      month: formatMonthLabel(snapshot.month),
      monthKey: snapshot.month,

      // Positive contributors
      contributions: changes.newContributions || 0,
      marketGrowth: Math.max(0, changes.marketGrowth || 0), // Only positive growth
      liabilityReduction: changes.liabilityReduction || 0,
      otherChanges: Math.max(0, changes.otherChanges || 0),

      // Negative contributors (shown as positive bars, different color)
      withdrawals: Math.abs(changes.withdrawals || 0),
      marketLosses: Math.abs(Math.min(0, changes.marketGrowth || 0)), // Only negative growth
      interestPaid: changes.interestPaid || 0,

      // Net change for reference line
      netChange: changes.totalChange || 0,

      // Raw values for tooltips
      _raw: changes
    };
  });
};

/**
 * Transform snapshots for account performance table
 * @param {Object} currentSnapshot - Current month snapshot
 * @param {Object} previousSnapshot - Previous month snapshot (optional)
 * @returns {Array} Table data with account details
 */
export const transformForAccountTable = (currentSnapshot, previousSnapshot = null) => {
  if (!currentSnapshot || !currentSnapshot.accounts) {
    return [];
  }

  const accounts = currentSnapshot.accounts || [];

  return accounts.map(account => {
    const openingBalance = account.openingBalance || 0;
    const closingBalance = account.closingBalance || 0;
    const netChange = account.netChange || 0;
    const changePercent = openingBalance !== 0
      ? (netChange / openingBalance) * 100
      : 0;

    return {
      id: account.accountId,
      accountName: account.accountName,
      accountType: account.accountType,
      accountPurpose: account.accountPurpose,

      openingBalance,
      closingBalance,
      netChange,
      changePercent,

      contributions: account.contributions || 0,
      withdrawals: account.withdrawals || 0,
      marketGrowth: account.marketGrowth || 0,
      dividendsInterest: account.dividendsInterest || 0,
      fees: account.fees || 0,

      // Color coding for growth
      growthColor: (account.marketGrowth || 0) >= 0 ? '#10b981' : '#ef4444',
      changeColor: netChange >= 0 ? '#10b981' : '#ef4444'
    };
  });
};

/**
 * Transform snapshots for liability breakdown table
 * @param {Object} currentSnapshot - Current month snapshot
 * @returns {Array} Table data with liability details
 */
export const transformForLiabilityTable = (currentSnapshot) => {
  if (!currentSnapshot || !currentSnapshot.liabilities) {
    return [];
  }

  const liabilities = currentSnapshot.liabilities || [];

  return liabilities.map(liability => {
    const openingBalance = liability.openingBalance || 0;
    const closingBalance = liability.closingBalance || 0;
    const reduction = openingBalance - closingBalance;
    const reductionPercent = openingBalance !== 0
      ? (reduction / openingBalance) * 100
      : 0;

    return {
      id: liability.liabilityId,
      name: liability.name,
      type: liability.type,

      openingBalance,
      closingBalance,
      reduction,
      reductionPercent,

      totalPayment: liability.totalPayment || 0,
      principalPaid: liability.principalPaid || 0,
      interestPaid: liability.interestPaid || 0,

      // Color coding
      reductionColor: reduction > 0 ? '#10b981' : '#ef4444'
    };
  });
};

/**
 * Transform asset allocation for pie chart
 * @param {Object} snapshot - Snapshot object
 * @returns {Array} Pie chart data
 */
export const transformForAssetAllocationChart = (snapshot) => {
  if (!snapshot || !snapshot.assetAllocation) {
    return [];
  }

  const allocation = snapshot.assetAllocation;

  const assetClasses = [
    { name: 'Stocks', value: allocation.stocks || 0, color: '#3b82f6' },
    { name: 'Bonds', value: allocation.bonds || 0, color: '#10b981' },
    { name: 'Real Estate', value: allocation.realEstate || 0, color: '#f59e0b' },
    { name: 'Commodities', value: allocation.commodities || 0, color: '#ef4444' },
    { name: 'Cryptocurrency', value: allocation.cryptocurrency || 0, color: '#8b5cf6' },
    { name: 'Cash', value: allocation.cash || 0, color: '#6b7280' }
  ];

  // Filter out zero values
  return assetClasses.filter(asset => asset.value > 0);
};

/**
 * Calculate summary statistics for display cards
 * @param {Object} currentSnapshot - Current month snapshot
 * @param {Object} previousSnapshot - Previous month snapshot (optional)
 * @returns {Object} Summary statistics
 */
export const calculateSummaryStats = (currentSnapshot, previousSnapshot = null) => {
  if (!currentSnapshot) {
    return {
      currentNetWorth: 0,
      monthOverMonthChange: 0,
      monthOverMonthPercent: 0,
      totalContributions: 0,
      totalMarketGrowth: 0,
      totalWithdrawals: 0,
      totalLiabilityReduction: 0,
      averageMonthlyGrowth: 0,
      growthRate: 0
    };
  }

  const changes = currentSnapshot.changes || {};

  const currentNetWorth = currentSnapshot.netWorth || 0;
  const monthOverMonthChange = changes.totalChange || 0;
  const monthOverMonthPercent = changes.totalChangePercent || 0;

  return {
    currentNetWorth,
    monthOverMonthChange,
    monthOverMonthPercent,

    totalContributions: changes.newContributions || 0,
    totalMarketGrowth: changes.marketGrowth || 0,
    totalWithdrawals: changes.withdrawals || 0,
    totalLiabilityReduction: changes.liabilityReduction || 0,
    totalInterestPaid: changes.interestPaid || 0,

    // Additional metrics
    netSavings: (changes.newContributions || 0) - (changes.withdrawals || 0),
    totalGrowth: (changes.marketGrowth || 0) + (changes.otherChanges || 0)
  };
};

/**
 * Calculate year-over-year comparison
 * @param {Array} snapshots - Array of snapshots
 * @param {string} currentMonth - Current month (YYYY-MM)
 * @returns {Object|null} YoY comparison or null
 */
export const calculateYearOverYear = (snapshots, currentMonth) => {
  if (!snapshots || !currentMonth) {
    return null;
  }

  // Find snapshot for same month last year
  const [year, month] = currentMonth.split('-');
  const lastYearMonth = `${parseInt(year) - 1}-${month}`;

  const currentSnapshot = snapshots.find(s => s.month === currentMonth);
  const lastYearSnapshot = snapshots.find(s => s.month === lastYearMonth);

  if (!currentSnapshot || !lastYearSnapshot) {
    return null;
  }

  const currentNetWorth = currentSnapshot.netWorth || 0;
  const lastYearNetWorth = lastYearSnapshot.netWorth || 0;
  const yoyChange = currentNetWorth - lastYearNetWorth;
  const yoyPercent = lastYearNetWorth !== 0
    ? (yoyChange / lastYearNetWorth) * 100
    : 0;

  return {
    currentNetWorth,
    lastYearNetWorth,
    yoyChange,
    yoyPercent,
    currentMonth: formatMonthLabel(currentMonth),
    lastYearMonth: formatMonthLabel(lastYearMonth)
  };
};

/**
 * Get data for comparison between two snapshots
 * @param {Object} snapshot1 - First snapshot (earlier)
 * @param {Object} snapshot2 - Second snapshot (later)
 * @returns {Object} Comparison data
 */
export const compareSnapshots = (snapshot1, snapshot2) => {
  if (!snapshot1 || !snapshot2) {
    return null;
  }

  const netWorth1 = snapshot1.netWorth || 0;
  const netWorth2 = snapshot2.netWorth || 0;
  const change = netWorth2 - netWorth1;
  const changePercent = netWorth1 !== 0 ? (change / netWorth1) * 100 : 0;

  return {
    period: {
      start: formatMonthLabel(snapshot1.month),
      end: formatMonthLabel(snapshot2.month)
    },
    netWorth: {
      start: netWorth1,
      end: netWorth2,
      change,
      changePercent
    },
    assets: {
      start: snapshot1.totalAssets || 0,
      end: snapshot2.totalAssets || 0,
      change: (snapshot2.totalAssets || 0) - (snapshot1.totalAssets || 0)
    },
    liabilities: {
      start: snapshot1.totalLiabilities || 0,
      end: snapshot2.totalLiabilities || 0,
      change: (snapshot2.totalLiabilities || 0) - (snapshot1.totalLiabilities || 0)
    }
  };
};

/**
 * Calculate average monthly growth rate
 * @param {Array} snapshots - Array of snapshots
 * @returns {Object} Growth rate statistics
 */
export const calculateAverageGrowth = (snapshots = []) => {
  if (!Array.isArray(snapshots) || snapshots.length < 2) {
    return {
      averageChange: 0,
      averagePercent: 0,
      averageContributions: 0,
      averageMarketGrowth: 0
    };
  }

  const sorted = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));

  let totalChange = 0;
  let totalPercent = 0;
  let totalContributions = 0;
  let totalMarketGrowth = 0;
  let count = 0;

  for (let i = 1; i < sorted.length; i++) {
    const changes = sorted[i].changes || {};

    totalChange += changes.totalChange || 0;
    totalPercent += changes.totalChangePercent || 0;
    totalContributions += changes.newContributions || 0;
    totalMarketGrowth += changes.marketGrowth || 0;
    count++;
  }

  return {
    averageChange: count > 0 ? totalChange / count : 0,
    averagePercent: count > 0 ? totalPercent / count : 0,
    averageContributions: count > 0 ? totalContributions / count : 0,
    averageMarketGrowth: count > 0 ? totalMarketGrowth / count : 0,
    periodCount: count
  };
};

/**
 * Get the latest N months of data
 * @param {Array} snapshots - Array of snapshots
 * @param {number} months - Number of months to include
 * @returns {Array} Filtered snapshots
 */
export const getRecentMonths = (snapshots = [], months = 12) => {
  if (!Array.isArray(snapshots)) {
    return [];
  }

  // Sort descending and take first N
  return [...snapshots]
    .sort((a, b) => b.month.localeCompare(a.month))
    .slice(0, months)
    .reverse(); // Reverse to get ascending order for charts
};

/**
 * Format currency for tooltips
 * @param {number} value - Value to format
 * @param {string} currencySymbol - Currency symbol
 * @returns {string} Formatted currency
 */
export const formatTooltipCurrency = (value, currencySymbol = '$') => {
  return formatCurrencyText(value, currencySymbol);
};

/**
 * Format percentage for tooltips
 * @param {number} value - Percentage value (as decimal or whole number)
 * @param {boolean} isDecimal - True if value is decimal (0.05 = 5%)
 * @returns {string} Formatted percentage
 */
export const formatTooltipPercent = (value, isDecimal = false) => {
  if (isDecimal) {
    return formatPercentage(value, 1);
  }
  return `${value.toFixed(1)}%`;
};
